import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useExtendedState from '../../hooks/useExtendedState';
import { userType, userPropertiesType } from '../../types';
import Privileges from '../../utils/Privileges';
import { hasPrivilege, Privileged } from '../../hocs/withPrivileges';
import PrivilegedCheckboxList from '../fields/privileged/PrivilegedCheckboxList';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import ReadonlyField from '../fields/ReadonlyField';
import DateTime, { DATETIME } from '../DateTime';
import './_styles_/UserDetailTab.css';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    USER_ADDRESS_MAX_LENGTH,
    USER_CODE_MAX_LENGTH,
    USER_EMAIL_MAX_LENGTH,
    USER_ID_MAX_LENGTH,
    USER_NAME_MAX_LENGTH,
    USER_PHONE_MAX_LENGTH,
    userAddressValidator,
    userCodeValidator,
    userEmailValidator,
    userIdValidator,
    userNameValidator,
    userPhoneValidator,
} from '../../validators/users';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_USERS_QUERY_NAME,
    UPDATE_USER,
    TOGGLE_USER_ACTIVATION,
    USER_DETAIL,
    TOGGLE_USER_SUSPENSION,
    USER_TOTP,
    DELETE_USER,
} from '../../graphql/users';
import ActivationButton from '../buttons/ActivationButton';
import UserMemberAssignment, { extractRealAssignableMembers } from './UserMemberAssignment';
import { createZoneItems, extractMemberZones, syncAssignedZonesWithMembers } from '../../utils/UserZones';
import {
    createRoleItem,
    handleBrokerWithBidder,
} from '../../utils/UserRoles';
import ExpandedListTab from '../ExpandedListTab';
import useAuthentication from '../../hooks/useAuthentication';
import InfoPanel from '../InfoPanel';
import {
    transformUserActivationBlockers,
    transformUserBlockers,
    transformUserDeactivationBlockers,
    USER_BLOCKER_AUCTION_PUBLISHED,
    USER_BLOCKER_USER_HAS_BIDS,
} from '../blockers/UserBlockers';
import SuspensionButton from '../buttons/SuspensionButton';
import useIsMounted from '../../hooks/useIsMounted';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Spinner from '../Spinner';
import MutationButton from "../buttons/MutationButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

export const translateLdapStatus = (t, ldapStatus) => {
    const {
        statusId,
        params,
    } = ldapStatus;
    let result;
    switch (statusId) {
        case 'ldap.status.usable':
            result = t('users.ldap-status-messages.usable');
            break;
        case 'ldap.status.password.expiration.days':
            result = t('users.ldap-status-messages.password-expiration-days', { days: params[0] });
            break;
        case 'ldap.status.password.expiration.seconds':
            result = t('users.ldap-status-messages.password-expiration-seconds', { seconds: params[0] });
            break;
        case 'ldap.status.inactive':
            result = t('users.ldap-status-messages.inactive');
            break;
        case 'ldap.status.expired':
            result = t('users.ldap-status-messages.expired');
            break;
        case 'ldap.status.locked':
            result = t('users.ldap-status-messages.locked');
            break;
        default:
            result = t('users.ldap-status-messages.unknown', { id: statusId });
    }
    return result;
};

UserDetailTab.propTypes = {
    user: userType.isRequired,
    userProps: userPropertiesType.isRequired,
};

function UserDetailTab(props) { // NOSONAR javascript:S3776
    const {
        user,
        userProps,
    } = props;
    const { t } = useTranslation();
    const {
        availableRoles,
        availableAssignedZones,
        assignableMembers,
        allowedRoleCombinations,
        clearingEnabled,
    } = userProps;
    const variables = useMemo(() => ({
        id: user.id,
    }), [user.id]);
    const [totpLoading, setTotpLoading] = useState(false);
    const [secondCounter, setSecondCounter] = useState(null);
    const [totpCode, setTotpCode] = useState(null);
    const {
        refetch,
    } = useExtendedQuery(USER_TOTP, {
        variables,
        variablesToUrl: false,
        skip: true,
    });

    useEffect(() => {
        return () => {
            if (secondCounter !== null) {
                clearInterval(secondCounter);
            }
        };
    }, [secondCounter]);

    const loadTotp = () => {
        refetch()
            .then((result) => {
                if (isMounted()) {
                    if (result.data.userTotp) {
                        const secondCounter = setInterval(() => {
                            const secondsSinceEpoch = new Date().getTime() / 1000;
                            const totpExpiresInSeconds = Math.ceil(result.data.userTotp.expiresSecondsSinceEpoch - secondsSinceEpoch);
                            const totp = `${result.data.userTotp.totp.substring(0, 3)} ${result.data.userTotp.totp.substring(3)}`;
                            setTotpCode(`${totp} (${totpExpiresInSeconds + 1})`);
                            if (totpExpiresInSeconds <= 0) {
                                clearInterval(secondCounter);
                                loadTotp();
                            }

                        }, 1000);
                        setSecondCounter(secondCounter);
                    } else {
                        setTotpCode(t('users.2fa-not-active'));
                    }
                }
            })
            .catch(() => {
                setTotpCode(null);
                setTotpLoading(false);
            });
    };

    const isMounted = useIsMounted();
    const activeState = useExtendedState(user.active);
    const suspendedState = useExtendedState(user.suspended);
    const userIdState = useExtendedState(user.racf);
    const userCodeState = useExtendedState(user.userCode);
    const nameState = useExtendedState(user.name);
    const emailState = useExtendedState(user.email);
    const phoneState = useExtendedState(user.phone);
    const addressState = useExtendedState(user.address);
    const deletedState = useExtendedState(user.deleted);
    const rolesState = useExtendedState(user.roles);
    const assignedMembersState = useExtendedState(user.members || []);
    const assignedZonesState = useExtendedState(
        user.assignedZones
            ? user.assignedZones.map((assignedZone) => assignedZone.code)
            : [],
    );
    const realAssignableMembersState = useExtendedState(extractRealAssignableMembers(availableRoles, assignableMembers, user.roles));
    const [isConflictingMember, setConflictingMember] = useState(false);
    const setNewAssignedMembers = (newAssignedMembers) => {
        const realAssignableMemberIds = realAssignableMembersState.value.map((realAssignableMember) => realAssignableMember.memberId);
        const isConflicting = newAssignedMembers.some((assignedMember) => !realAssignableMemberIds.includes(assignedMember.memberId));
        setConflictingMember(isConflicting);
        assignedZonesState.setValue(syncAssignedZonesWithMembers(assignedZonesState.value, assignedMembersState.value, newAssignedMembers));
        assignedMembersState.setValue(newAssignedMembers);
    };
    const resetAssignedMembersAndZones = () => {
        assignedMembersState.reset();
        assignedZonesState.reset();
    };
    const setNewRoles = (newRoles) => {
        const newRolesToSet = handleBrokerWithBidder(rolesState.value, newRoles);
        rolesState.setValue(newRolesToSet);
        const newRealAssignableMembers = extractRealAssignableMembers(availableRoles, assignableMembers, newRolesToSet);
        const newRealAssignableMemberIds = newRealAssignableMembers.map((newRealAssignableMember) => newRealAssignableMember.memberId);
        const isConflicting = assignedMembersState.value.some((assignedMember) => !newRealAssignableMemberIds.includes(assignedMember.memberId));
        setConflictingMember(isConflicting);
        realAssignableMembersState.setValue(newRealAssignableMembers);
    };
    const resetRolesByUser = () => {
        rolesState.reset();
        realAssignableMembersState.reset();
    };
    const canAssignMembersToUser = rolesState.value.some((role) => {
        const foundAvailableRole = availableRoles.find((availableRole) => availableRole.role === role);
        return foundAvailableRole
            && foundAvailableRole.allowedMemberTypes
            && foundAvailableRole.allowedMemberTypes.length > 0;
    });
    const assignedZoneOptions = createZoneItems(extractMemberZones(assignedMembersState.value));
    // with less than two zones in the system there is nothing to choose from, so the zones are not offered
    // and the user is simply assigned all zones of their members
    const assignedZonesAreSelectable = availableAssignedZones != null;
    const assignedZonesToSubmit = assignedZonesAreSelectable
        ? assignedZonesState.value
        : assignedZoneOptions.map((assignedZoneOption) => assignedZoneOption.id);
    const { validators, isValid } = useMultiValidator(true, {
        userId: compositeValidator(userIdValidator(t), wafValidator(t)),
        userCode: compositeValidator(userCodeValidator(t), wafValidator(t)),
        name: compositeValidator(userNameValidator(t), wafValidator(t)),
        email: compositeValidator(userEmailValidator(t), wafValidator(t)),
        phone: userPhoneValidator(t),
        address: compositeValidator(userAddressValidator(t), wafValidator(t))
    });
    const isChanged = userIdState.isChanged || nameState.isChanged || userCodeState.isChanged || emailState.isChanged || phoneState.isChanged || addressState.isChanged || rolesState.isChanged || assignedMembersState.isChanged || assignedZonesState.isChanged;
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();
    const initialValidity = isConflictingMember && canAssignMembersToUser ? Promise.reject() : Promise.resolve();
    const readonlyPromise = user.deleted ? Promise.reject() : Promise.resolve();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([readonlyPromise, initialValidity, isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    let toggleSuspendUserConfirmMessageKey = 'users.confirm-suspend-user';
    let toggleSuspendUserFailureMessageKey = 'users.notification.user-suspend-failure';
    if (suspendedState.value) {
        toggleSuspendUserConfirmMessageKey = 'users.confirm-unsuspend-user';
        toggleSuspendUserFailureMessageKey = 'users.notification.user-unsuspend-failure';
    }

    const toggleSuspendUserMutation = useExtendedMutation(TOGGLE_USER_SUSPENSION, {
        variables: { id: user.id },
        successCallback: () => {
            if (isMounted()) {
                suspendedState.changeInitialState(!suspendedState.value);
                suspendedState.reset();
            }
        },
        successMessageCreator: ({ toggleUserSuspension: { suspended: updatedSuspended, name: updatedName, racf: updatedUserId } }) => {
            let toggleSuspendUserSuccessMessageKey = 'users.notification.user-unsuspend-success';
            if (updatedSuspended) {
                toggleSuspendUserSuccessMessageKey = 'users.notification.user-suspend-success';
            }
            return t(toggleSuspendUserSuccessMessageKey, { racf: updatedUserId, name: updatedName });
        },
        failureMessageOptions: { key: toggleSuspendUserFailureMessageKey, params: { name: user.name, racf: user.racf } },
        refetchQueries: [ALL_USERS_QUERY_NAME, {
            query: USER_DETAIL,
            variables: { id: user.id },
        }],
    });

    const deleteUserMutation = useExtendedMutation(DELETE_USER, {
        variables: { id: user.id },
        successCallback: (r) => {
            nameState.setValue(r.deleteUser.name);
            emailState.setValue(r.deleteUser.email);
            phoneState.setValue(r.deleteUser.phone);
            addressState.setValue(r.deleteUser.address);
            deletedState.setValue(r.deleteUser.deleted);
            assignedMembersState.setValue(r.deleteUser.members || []);
            assignedZonesState.setValue((r.deleteUser.assignedZones || []).map((assignedZone) => assignedZone.code));
        },
        successMessageCreator: ({ deleteUser: { racf: updatedUserId } }) => {
            return t('users.notification.user-delete-success', { racf: updatedUserId });
        },
        failureMessageOptions: { key: 'users.notification.user-delete-failure', params: { name: user.name, racf: user.racf } },
        refetchQueries: [ALL_USERS_QUERY_NAME, {
            query: USER_DETAIL,
            variables: { id: user.id },
        }],
    });

    const isActivationButtonDisabled = user.deleted || (activeState.value && user.deactivationBlockers?.length > 0)
        || (!activeState.value && user.activationBlockers?.length > 0);
    const isDeleteButtonDisabled = user.deleted || ((activeState.value && user.deactivationBlockers?.length > 0) && !user.internal);
    let toggleUserActivationConfirmMessageKey = 'users.confirm-activate-user';
    let toggleUserActivationFailureMessageKey = 'users.notification.user-activate-failure';
    if (activeState.value) {
        toggleUserActivationConfirmMessageKey = 'users.confirm-deactivate-user';
        toggleUserActivationFailureMessageKey = 'users.notification.user-deactivate-failure';
    }
    const toggleUserActivationMutation = useExtendedMutation(TOGGLE_USER_ACTIVATION, {
        variables: { id: user.id },
        successCallback: () => {
            if (isMounted()) {
                activeState.changeInitialState(!activeState.value);
                activeState.reset();
            }
        },
        successMessageCreator: ({ toggleUserActivation: { active: updatedActive, name: updatedName, racf: updatedRacf } }) => {
            let toggleUserActivationSuccessMessageKey = 'users.notification.user-deactivate-success';
            if (updatedActive) {
                toggleUserActivationSuccessMessageKey = 'users.notification.user-activate-success';
            }
            return t(toggleUserActivationSuccessMessageKey, { racf: updatedRacf, name: updatedName });
        },
        failureMessageOptions: { key: toggleUserActivationFailureMessageKey, params: { name: user.name, racf: user.racf } },
        refetchQueries: [ALL_USERS_QUERY_NAME, {
            query: USER_DETAIL,
            variables: { id: user.id },
        }],
    });

    const updateUserMutation = useExtendedMutation(UPDATE_USER, {
        variables: {
            id: user.id,
            racf: user.internal ? null : userIdState.value,
            userCode: clearingEnabled ? userCodeState.value : null,
            name: user.internal ? null : nameState.value,
            email: user.internal ? null : emailState.value,
            phone: user.internal ? null : phoneState.value,
            address: user.internal ? null : addressState.value,
            roles: user.internal ? null : rolesState.value,
            memberIds: canAssignMembersToUser
                ? assignedMembersState.value.map((assignedMember) => assignedMember.memberId)
                : null,
            assignedZones: canAssignMembersToUser ? assignedZonesToSubmit : null,
        },
        statesToResetOnSuccess: [userIdState, userCodeState, nameState, emailState, phoneState, addressState, rolesState, assignedMembersState, assignedZonesState, realAssignableMembersState],
        successMessageOptions: { key: 'users.notification.user-update-success', dataProperty: 'updateUser' },
        failureMessageOptions: { key: 'users.notification.user-update-failure', params: { name: user.name, racf: user.racf } },
        refetchQueries: [ALL_USERS_QUERY_NAME, {
            query: USER_DETAIL,
            variables: { id: user.id },
        }],
    });
    const rolesWarningGenerator = () => {
        let result = null;
        if (user.members && user.members.length > 0 && !canAssignMembersToUser) {
            result = 'users.warning.members-will-be-unassigned';
        }
        return result;
    };
    const sufficientPrivileges = [Privileges.USER_EDIT];
    const { userPrivileges } = useAuthentication();
    const availableRolesMap = (user.internal || !hasPrivilege(userPrivileges, sufficientPrivileges))
        ? user.roles.map((assignedRole) => createRoleItem(t, assignedRole))
        : availableRoles.filter((availableRole) => availableRole.isAssignable).map((assignableRole) => createRoleItem(t, assignableRole.role));
    const canModifyUserCode = !user.modificationBlockers?.includes(USER_BLOCKER_AUCTION_PUBLISHED)
        && !user.modificationBlockers?.includes(USER_BLOCKER_USER_HAS_BIDS);
    const canModifyUserID = !user.modificationBlockers?.includes(USER_BLOCKER_USER_HAS_BIDS);
    const isSuspendButtonDisabled = user.deleted || user.suspensionBlockers?.length > 0;
    const membersAreShown = !user.deleted && (hasPrivilege(userPrivileges, sufficientPrivileges) ? canAssignMembersToUser : user.members != null);
    const assignedZonesAreShown = !user.deleted && (hasPrivilege(userPrivileges, sufficientPrivileges)
        ? canAssignMembersToUser && assignedZonesAreSelectable && assignedZoneOptions.length > 0
        : user.assignedZones != null);
    return (
        <ExpandedListTab
            className="user-detail-tab"
            title={t('users.user-detail')}
        >
            <div className="user-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('users.password-retry-count')}
                    value={user.passwordRetryCount || t('misc.na')}
                    className="user-detail-tab--fields--password-retry-count"
                />
                <ReadonlyField
                    label={t('users.last-login-time')}
                    value={user.lastSigninTime ? <DateTime value={user.lastSigninTime} format={DATETIME} /> : t('misc.na')}
                    className="user-detail-tab--fields--last-login-time"
                />
                <ReadonlyField
                    label={t('users.suspended')}
                    value={suspendedState.value ? t('misc.yes') : t('misc.no')}
                    className="user-detail-tab--fields--suspended"
                />
                <ReadonlyField
                    label={t('users.active')}
                    value={activeState.value ? t('misc.yes') : t('misc.no')}
                    className="user-detail-tab--fields--active"
                />
                {user.ldapStatuses && (
                    <div className="field readonly user-detail-tab--fields--ldap-status">
                        <span className="field--key">{t('users.ldap-status')}</span>
                        <div className="field--value values">
                            {user.ldapStatuses.map((ldapStatus) => <div key={ldapStatus.statusId}>{translateLdapStatus(t, ldapStatus)}</div>)}
                        </div>
                    </div>
                )}
                {!user.deleted && !user.internal && hasPrivilege(userPrivileges, sufficientPrivileges) && (
                    <>
                        <div className="field readonly user-detail-tab--fields--totp">
                            <span className="field--key">{t('twoFA.token')}</span>
                            <div className="field--value values">
                                {!totpCode ? <button className="button button-bordered-orange" onClick={() => {setTotpLoading(true); loadTotp();}}>{totpLoading ? <Spinner size={8} displayLabel={false} /> : t('misc.show')}</button> : '' }
                                {totpCode ? <span>{totpCode}</span> : ''}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="user-detail-tab--fields">
                <PrivilegedTextField
                    sufficientPrivileges={user.internal || !canModifyUserID ? [] : sufficientPrivileges}
                    inputId="user-detail--user-id"
                    label={t('users.user-id')}
                    state={userIdState}
                    className="user-detail-tab--fields--user-id"
                    maxLength={USER_ID_MAX_LENGTH}
                    validator={validators.userId}
                    placeholder={t('users.placeholder.user-id')}
                    isRequired
                    readonly={user.deleted}
                />
                {clearingEnabled && (
                    <PrivilegedTextField
                        sufficientPrivileges={!canModifyUserCode ? [] : sufficientPrivileges}
                        inputId="user-detail--user-code"
                        label={t('users.user-code')}
                        state={userCodeState}
                        className="user-detail-tab--fields--user-code"
                        maxLength={USER_CODE_MAX_LENGTH}
                        validator={validators.userCode}
                        placeholder={t('users.placeholder.user-code')}
                        isRequired
                        readonly={user.deleted}
                    />
                )}
                <PrivilegedTextField
                    sufficientPrivileges={user.internal ? [] : sufficientPrivileges}
                    label={t('users.name')}
                    state={nameState}
                    validator={validators.name}
                    className={`user-detail-tab--fields--name wide ${user.deleted ? 'deleted' : ''}`}
                    maxLength={USER_NAME_MAX_LENGTH}
                    placeholder={t('users.placeholder.name')}
                    isRequired
                    readonly={user.deleted}
                />
                <PrivilegedTextField
                    sufficientPrivileges={user.internal ? [] : sufficientPrivileges}
                    label={t('users.email')}
                    state={emailState}
                    validator={validators.email}
                    className={`user-detail-tab--fields--email wide ${user.deleted ? 'deleted' : ''}`}
                    maxLength={USER_EMAIL_MAX_LENGTH}
                    inputMode="email"
                    placeholder={t('users.placeholder.email')}
                    isRequired
                    readonly={user.deleted}
                />
                <PrivilegedTextField
                    sufficientPrivileges={user.internal ? [] : sufficientPrivileges}
                    label={t('users.phone')}
                    state={phoneState}
                    validator={validators.phone}
                    className={`user-detail-tab--fields--phone ${user.deleted ? 'deleted' : ''}`}
                    maxLength={USER_PHONE_MAX_LENGTH}
                    inputMode="tel"
                    placeholder={t('users.placeholder.phone')}
                    readonly={user.deleted}
                />
                <PrivilegedTextField
                    sufficientPrivileges={user.internal ? [] : sufficientPrivileges}
                    label={t('users.address')}
                    state={addressState}
                    validator={validators.address}
                    className={`user-detail-tab--fields--address x-wide ${user.deleted ? 'deleted' : ''}`}
                    maxLength={USER_ADDRESS_MAX_LENGTH}
                    placeholder={t('users.placeholder.address')}
                    readonly={user.deleted}
                />
            </div>
            <div className="user-detail-tab--roles">
                <PrivilegedCheckboxList
                    sufficientPrivileges={user.internal ? [] : sufficientPrivileges}
                    label={t('users.roles')}
                    name="roles-list"
                    values={availableRolesMap}
                    allowedCombinations={allowedRoleCombinations}
                    state={rolesState}
                    onChange={setNewRoles}
                    className="user-detail-tab--fields--roles"
                    onReset={resetRolesByUser}
                    warningGenerator={rolesWarningGenerator}
                    readonly={user.deleted}
                />
            </div>
            {assignedZonesAreShown && (
                <PrivilegedCheckboxList
                    sufficientPrivileges={sufficientPrivileges}
                    label={t('users.user-assigned-zones')}
                    name="user-assigned-zones-list"
                    values={assignedZoneOptions}
                    state={assignedZonesState}
                    className="user-detail-tab--fields--user-assigned-zones"
                    inputId="user-detail--user-assigned-zones"
                />
            )}
            {membersAreShown && (
                <div className="user-detail-tab--members">
                    <UserMemberAssignment
                        assignedMembers={assignedMembersState.value}
                        assignableMembers={realAssignableMembersState.value}
                        onMemberAssignment={setNewAssignedMembers}
                        isChanged={assignedMembersState.isChanged}
                        onReset={resetAssignedMembersAndZones}
                    />
                </div>
            )}
            <Privileged sufficientPrivileges={sufficientPrivileges}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateUserMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('users.confirm-update-user', { name: user.name, userId: user.racf })}
                            extraConfirmationMessage={t('users.extra-confirm-update-user', { name: user.name, userId: user.racf })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <SuspensionButton
                            isSuspended={suspendedState.value}
                            extendedMutation={toggleSuspendUserMutation}
                            confirmationMessage={t(toggleSuspendUserConfirmMessageKey, { name: user.name, userId: user.racf })}
                            extraConfirmationMessage={t('users.extra-confirm-suspend-user', { name: user.name, userId: user.racf })}
                            disabled={isSuspendButtonDisabled}
                        />
                        <ActivationButton
                            isActive={activeState.value}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleUserActivationMutation}
                            confirmationMessage={t(toggleUserActivationConfirmMessageKey, { name: user.name, userId: user.racf })}
                        />
                        <MutationButton
                            className="delete-button button-filled-red button"
                            confirmationMessage={t('users.confirm-delete-user', { name: user.name, userId: user.racf })}
                            extendedMutation={deleteUserMutation}
                            showConfirmationDialog
                            disabled={isDeleteButtonDisabled}>
                            <FontAwesomeIcon icon="trash-alt" />&nbsp;{t('button.delete')}
                        </MutationButton>
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {user.modificationBlockers?.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformUserBlockers(t, modificationBlocker)}
                    />
                ))}
                {user.suspensionBlockers?.map((suspensionBlocker) => (
                    <InfoPanel
                        key={suspensionBlocker}
                        message={transformUserBlockers(t, suspensionBlocker)}
                    />
                ))}
                {user.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformUserDeactivationBlockers(t, deactivationBlocker)}
                    />
                ))}
                {user.activationBlockers?.map((activationBlocker) => (
                    <InfoPanel
                        key={activationBlocker}
                        message={transformUserActivationBlockers(t, activationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default UserDetailTab;
